import React, { useState, useMemo, useEffect, useCallback, memo } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment,
  Checkbox,
  IconButton,
} from "@material-ui/core";
import { Skeleton, Pagination, Alert } from "@material-ui/lab";
import {
  GetApp as GetAppIcon,
  FileCopy as FileCopyIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from "@material-ui/icons";
import { styled } from "@material-ui/core/styles";
import { getBellCadParcelUrl } from "../../../utils/map";

// Styled components that are still being used
const StyledDialog = styled(Dialog)(() => ({
  "& .MuiDialog-paper": {
    maxWidth: "95vw",
    width: "1200px",
    maxHeight: "90vh",
  },
}));

const SummaryCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  marginBottom: theme.spacing(2),
}));

const ComparisonContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

// Reusable style constants to avoid repetition
const COMMON_STYLES = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  labelStyle: {
    fontWeight: 500,
    color: "rgba(0, 0, 0, 0.6)",
    minWidth: 120,
    fontSize: "0.875rem",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  valueStyle: {
    color: "rgba(0, 0, 0, 0.87)",
    wordBreak: "break-word",
    fontSize: "0.875rem",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  sectionHeaderStyle: {
    fontWeight: 600,
    marginBottom: 4,
    padding: 4,
    borderRadius: "4px 4px 0 0",
    fontSize: "1rem",
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  },
  cardStyle: {
    marginBottom: 8,
    border: "1px solid #1976d2",
    borderLeft: "4px solid #1976d2",
    boxShadow:
      "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
  },
};

// Memoized info row component
const InfoRow = memo(({ label, value }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      padding: 4,
      borderBottom: "1px solid #f0f0f0",
    }}
  >
    <span style={COMMON_STYLES.labelStyle}>{label}:</span>
    <span style={COMMON_STYLES.valueStyle}>{value}</span>
  </div>
));

// Memoized section component
const InfoSection = memo(({ title, children, color = "#1976d2" }) => (
  <div
    style={{
      height: "100%",
      border: "1px solid rgba(0, 0, 0, 0.12)",
      borderRadius: "4px",
    }}
  >
    <div
      style={{
        ...COMMON_STYLES.sectionHeaderStyle,
        color,
        backgroundColor: `${color}04`,
        borderBottom: `1px solid ${color}`,
      }}
    >
      {title}
    </div>
    <div>{children}</div>
  </div>
));

// Optimized well card component with extracted styling
const WellDataCard = memo(
  ({ wellData, selectedWells, handleSelectWell }) => (
    <Card style={COMMON_STYLES.cardStyle}>
      <div style={{ padding: 12 }}>
        {/* Header Section */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            marginBottom: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              margin: 0,
              alignSelf: "flex-start",
              marginRight: 8,
            }}
          >
            <Checkbox
              checked={selectedWells.has(wellData.id)}
              onChange={(e) => handleSelectWell(wellData.id, e.target.checked)}
              color="primary"
              style={{ padding: 9 }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <h2
              style={{
                margin: "0 0 6px 0",
                fontSize: "1.25rem",
                fontWeight: 500,
                color: "#1976d2",
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              }}
            >
              Well #{wellData.id}: {wellData.wellNumber}
              {wellData.parcelId === "N/A" && (
                <Chip
                  label="No Parcel Match"
                  size="small"
                  style={{
                    backgroundColor: "#f44336",
                    color: "#ffffff",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    marginLeft: 12,
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  }}
                />
              )}
            </h2>
            <p
              style={{
                margin: "0 0 3px 0",
                fontSize: "0.875rem",
                color: "rgba(0, 0, 0, 0.6)",
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              }}
            >
              {wellData.wellName} • {wellData.wellStatus} •{" "}
              {wellData.primaryUse}
            </p>
            <p
              style={{
                margin: 0,
                fontSize: "0.875rem",
                color: "rgba(0, 0, 0, 0.6)",
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
              }}
            >
              {wellData.aquifer} • {wellData.location}
            </p>
          </div>
        </div>

        {/* Technical Information Section */}
        <div style={{ marginBottom: 12 }}>
          <InfoSection title="Well Technical Information">
            <div style={{ display: "flex", padding: "4px 0" }}>
              <div style={{ flex: 1, paddingRight: 6 }}>
                <InfoRow label="CUWCD Number" value={wellData.wellNumber} />
                <InfoRow
                  label="State Number"
                  value={wellData.stateWellNumber}
                />
                <InfoRow label="Status" value={wellData.wellStatus} />
                <InfoRow label="Primary Use" value={wellData.primaryUse} />
                <InfoRow label="Source Aquifer" value={wellData.aquifer} />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 4,
                  }}
                >
                  <span style={COMMON_STYLES.labelStyle}>Coordinates:</span>
                  <span style={COMMON_STYLES.valueStyle}>
                    {wellData.location}
                  </span>
                </div>
              </div>
              <div style={{ flex: 1, paddingLeft: 6 }}>
                <InfoRow label="Well Depth" value={wellData.wellDepthFt} />
                <InfoRow label="Elevation" value={wellData.elevationFtAmsl} />
                <InfoRow
                  label="Screen Top Depth"
                  value={wellData.screenTopDepthFt}
                />
                <InfoRow
                  label="Screen Bottom Depth"
                  value={wellData.screenBottomDepthFt}
                />
                <InfoRow label="Driller" value={wellData.driller} />
                <InfoRow label="Date Drilled" value={wellData.dateDrilled} />
                <InfoRow label="Permit Number" value={wellData.permitNumber} />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 4,
                  }}
                >
                  <span style={COMMON_STYLES.labelStyle}>Well Type:</span>
                  <span style={COMMON_STYLES.valueStyle}>
                    {wellData.wellType}
                  </span>
                </div>
              </div>
            </div>
          </InfoSection>
        </div>

        {/* Owner Information Section */}
        <div style={{ display: "flex", gap: 8 }}>
          {/* Well Owner */}
          <div style={{ flex: 1 }}>
            <div
              style={{
                height: "100%",
                border: "1px solid rgba(0, 0, 0, 0.12)",
                borderRadius: "4px",
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  color: "#1976d2",
                  marginBottom: 4,
                  padding: 4,
                  backgroundColor: "rgba(25, 118, 210, 0.04)",
                  borderBottom: "1px solid #1976d2",
                  fontSize: "1rem",
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                }}
              >
                Current Well Owner (Database Records)
              </div>
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    Owner Name:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.wellOwnerName}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    Organization:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.wellOwnerOrganization}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    Address:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.wellOwnerAddress}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    City, State ZIP:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.wellOwnerCity}, {wellData.wellOwnerState}{" "}
                    {wellData.wellOwnerZip}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    Phone 1:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.wellOwnerPhone1}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    Phone 2:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.wellOwnerPhone2}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    Email 1:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.wellOwnerEmail1}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    Email 2:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.wellOwnerEmail2}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Parcel Owner */}
          <div style={{ flex: 1 }}>
            <div
              style={{
                height: "100%",
                border: "1px solid rgba(0, 0, 0, 0.12)",
                borderRadius: "4px",
              }}
            >
              <div
                style={{
                  fontWeight: 600,
                  color: "#7b1fa2",
                  marginBottom: 4,
                  padding: 4,
                  backgroundColor: "rgba(123, 31, 162, 0.04)",
                  borderBottom: "1px solid #7b1fa2",
                  fontSize: "1rem",
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                }}
              >
                Parcel Owner (County Records)
                {wellData.propertyOwnerName === "Not found" && (
                  <Chip
                    label="No Parcel Contact"
                    size="small"
                    style={{
                      backgroundColor: "#ff9800",
                      color: "#ffffff",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      marginLeft: 12,
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  />
                )}
              </div>
              <div>
                <div
                  style={{
                    fontSize: "0.875rem",
                    color: "#7b1fa2",
                    padding: 4,
                    fontWeight: 600,
                  }}
                >
                  Current Parcel Owner
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    Owner Name:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.propertyOwnerName}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    Mailing Address:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.propertyOwnerAddress}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    City, State ZIP:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.propertyOwnerCity}, {wellData.propertyOwnerState}{" "}
                    {wellData.propertyOwnerZip}
                  </span>
                </div>

                <div
                  style={{
                    fontSize: "0.875rem",
                    color: "#7b1fa2",
                    padding: "4px 4px 0 4px",
                    fontWeight: 600,
                    marginTop: 12,
                  }}
                >
                  Parcel Information
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    Parcel ID:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.parcelId !== "N/A" ? (
                      <a
                        href={getBellCadParcelUrl(wellData.parcelId)}
                        target="_blank"
                        rel="noreferrer noopener"
                        style={{
                          color: "#1976d2",
                          textDecoration: "underline",
                          fontFamily:
                            '"Roboto", "Helvetica", "Arial", sans-serif',
                        }}
                      >
                        {wellData.parcelId}
                      </a>
                    ) : (
                      wellData.parcelId
                    )}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    Legal Acreage:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.parcelAcres} acres
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    Parcel Address:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.situsAddress}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    Parcel City:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.situsCity}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    Legal Description:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.legalDescription}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    Market Value:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.marketValue}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    Land Value:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.landValue}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    Improvement Value:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.improvementValue}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: 6,
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      color: "rgba(0, 0, 0, 0.6)",
                      minWidth: 120,
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    Last Deed Date:
                  </span>
                  <span
                    style={{
                      color: "rgba(0, 0, 0, 0.87)",
                      wordBreak: "break-word",
                      fontSize: "0.875rem",
                      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    }}
                  >
                    {wellData.lastDeedDate}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  ),
  (prevProps, nextProps) => {
    return (
      prevProps.wellData === nextProps.wellData &&
      prevProps.selectedWells.has(prevProps.wellData.id) ===
        nextProps.selectedWells.has(nextProps.wellData.id)
    );
  }
);

// Constants that don't need component scope
const WELLS_PER_PAGE = 10;

const CSV_HEADERS = [
  "Well Number",
  "State Number",
  "Well Name",
  "Location",
  "Well Status",
  "Primary Use",
  "Aquifer",
  "Well Depth (ft)",
  "Elevation (ft AMSL)",
  "Screen Top Depth (ft)",
  "Screen Bottom Depth (ft)",
  "Driller",
  "Date Drilled",
  "Permit Number",
  "Well Type",
  "Current Well Owner Name",
  "Current Well Owner Organization",
  "Current Well Owner Address",
  "Current Well Owner City",
  "Current Well Owner State",
  "Current Well Owner ZIP",
  "Current Well Owner Phone 1",
  "Current Well Owner Phone 2",
  "Current Well Owner Email 1",
  "Current Well Owner Email 2",
  "Property Owner Name",
  "Property Owner Address",
  "Property Owner City",
  "Property Owner State",
  "Property Owner ZIP",
  "Parcel ID",
  "Parcel Acres",
  "Situs Address",
  "Situs City",
  "Legal Description",
  "Market Value",
  "Land Value",
  "Improvement Value",
  "Last Deed Date",
];

const WellsReportDialog = ({
  open,
  onClose,
  reportData,
  loading,
  searchCoordinates,
  searchRadius,
  searchUnits,
}) => {
  const [searchInput, setSearchInput] = useState(""); // User input (immediate)
  const [searchTerm, setSearchTerm] = useState(""); // Debounced search term
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedWells, setSelectedWells] = useState(new Set()); // Track selected wells by ID

  // Prepare data even during loading to maintain hook order
  const {
    wells = [],
    wellsCount = 0,
    parcelsFoundCount = 0,
  } = reportData || {};

  // Handle search input changes (immediate UI update)
  const handleSearchChange = useCallback((event) => {
    setSearchInput(event.target.value);
  }, []);

  const handlePageChange = useCallback((event, page) => {
    setCurrentPage(page);
  }, []);

  // Selection handlers
  const handleSelectWell = useCallback((wellId, checked) => {
    setSelectedWells((prev) => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(wellId);
      } else {
        newSelected.delete(wellId);
      }
      return newSelected;
    });
  }, []);

  const handleSelectNone = useCallback(() => {
    setSelectedWells(new Set());
  }, []);

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1); // Reset to page 1 when search changes
    }, 300); // 300ms delay

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Initialize all wells as selected when NEW data loads (reset on new query)
  useEffect(() => {
    if (wells.length > 0) {
      const allWellIds = new Set(
        Array.from({ length: wells.length }, (_, i) => i + 1)
      );
      setSelectedWells(allWellIds);
    } else {
      setSelectedWells(new Set());
    }
  }, [wells.length]); // Remove selectedWells.size dependency to avoid interference

  // Clear search input when dialog is closed
  useEffect(() => {
    if (!open) {
      setSearchInput("");
      setSearchTerm("");
      setCurrentPage(1);
    }
  }, [open]);

  // Data is now pre-transformed by the backend, no need for client-side transformation
  const allTableData = useMemo(() => {
    return wells || [];
  }, [wells]);

  // Filter and paginate data
  const filteredData = useMemo(() => {
    if (!searchTerm) return allTableData;

    const searchLower = searchTerm.toLowerCase();
    return allTableData.filter(
      (item) =>
        item.wellNumber.toLowerCase().includes(searchLower) ||
        item.wellName.toLowerCase().includes(searchLower) ||
        item.wellOwnerName.toLowerCase().includes(searchLower) ||
        item.propertyOwnerName.toLowerCase().includes(searchLower) ||
        item.wellStatus.toLowerCase().includes(searchLower) ||
        item.primaryUse.toLowerCase().includes(searchLower)
    );
  }, [allTableData, searchTerm]);

  const startIndex = useMemo(
    () => (currentPage - 1) * WELLS_PER_PAGE,
    [currentPage]
  );

  const paginatedData = useMemo(
    () => filteredData.slice(startIndex, startIndex + WELLS_PER_PAGE),
    [filteredData, startIndex]
  );

  // Memoize the filtered well IDs to avoid recreating the Set on every render
  const filteredWellIds = useMemo(
    () => new Set(filteredData.map((item) => item.id)),
    [filteredData]
  );

  // Regular function to select all visible wells (not a hook)
  const selectAllVisible = useCallback(() => {
    setSelectedWells((prev) => {
      const newSelected = new Set(prev);
      filteredWellIds.forEach((id) => newSelected.add(id));
      return newSelected;
    });
  }, [filteredWellIds]);

  // Calculate filtered parcel count
  const filteredParcelsCount = useMemo(
    () =>
      filteredData.filter((item) => item.propertyOwnerName !== "Not found")
        .length,
    [filteredData]
  );

  // Calculate selected data for exports
  const selectedData = useMemo(
    () => allTableData.filter((item) => selectedWells.has(item.id)),
    [allTableData, selectedWells]
  );

  const selectedParcelsCount = useMemo(
    () =>
      selectedData.filter((item) => item.propertyOwnerName !== "Not found")
        .length,
    [selectedData]
  );

  const totalPages = useMemo(
    () => Math.ceil(filteredData.length / WELLS_PER_PAGE),
    [filteredData]
  );

  // Memoized CSV export function
  const exportToCSV = useCallback(() => {
    const csvContent = [
      CSV_HEADERS.join(","),
      ...selectedData.map((row) =>
        [
          row.wellNumber,
          row.stateWellNumber,
          row.wellName,
          row.location,
          row.wellStatus,
          row.primaryUse,
          row.aquifer,
          row.wellDepthFt,
          row.elevationFtAmsl,
          row.screenTopDepthFt,
          row.screenBottomDepthFt,
          row.driller,
          row.dateDrilled,
          row.permitNumber,
          row.wellType,
          row.wellOwnerName,
          row.wellOwnerOrganization,
          row.wellOwnerAddress,
          row.wellOwnerCity,
          row.wellOwnerState,
          row.wellOwnerZip,
          row.wellOwnerPhone1,
          row.wellOwnerPhone2,
          row.wellOwnerEmail1,
          row.wellOwnerEmail2,
          row.propertyOwnerName,
          row.propertyOwnerAddress,
          row.propertyOwnerCity,
          row.propertyOwnerState,
          row.propertyOwnerZip,
          row.parcelId,
          row.parcelAcres,
          row.situsAddress,
          row.situsCity,
          row.legalDescription,
          row.marketValue,
          row.landValue,
          row.improvementValue,
          row.lastDeedDate,
        ]
          .map((field) => `"${field}"`)
          .join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `Well_Owner_Contact_Lists_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [selectedData]);

  // Memoized PDF export function
  const exportToPDF = useCallback(() => {
    // Create a new window with printable content
    const printWindow = window.open("", "_blank");
    const htmlContent = `
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <title>Current Well Owner / Parcel Owner Report - ${
            new Date().toISOString().split("T")[0]
          }</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 18px; font-size: 13px; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 1px solid #333; padding-bottom: 12px; }
            .header h1 { margin: 8px 0; font-size: 20px; }
            .header p { margin: 4px 0; font-size: 12px; }
            .summary { background: #f5f5f5; padding: 12px; margin-bottom: 18px; font-size: 12px; }
            .well-card { border: 1px solid #ddd; margin-bottom: 18px; page-break-inside: avoid; page-break-after: always; }
            .well-header { background: #e3f2fd; padding: 8px; font-weight: bold; color: #1976d2; font-size: 14px; }
            .section { margin-bottom: 12px; }
            .section-title { background: #f5f5f5; padding: 6px; font-weight: bold; border-bottom: 1px solid #ddd; font-size: 13px; }
            .info-table { width: 100%; border-collapse: collapse; margin-bottom: 10px; font-size: 12px; }
            .info-table td { padding: 4px 7px; border-bottom: 1px solid #eee; }
            .info-table td:first-child { font-weight: bold; width: 150px; }
            .comparison { display: flex; gap: 18px; }
            .comparison > div { flex: 1; }
            .technical-columns { display: flex; gap: 18px; }
            .technical-columns > div { flex: 1; }
            .chip-red { background: #f44336; color: white; padding: 2px 8px; border-radius: 12px; font-size: 10px; display: inline-block; margin-left: 8px; font-weight: 500; }
            .chip-orange { background: #ff9800; color: white; padding: 2px 8px; border-radius: 12px; font-size: 10px; display: inline-block; margin-left: 8px; font-weight: 500; }
            .data-sources {
              position: fixed;
              bottom: 0;
              left: 0;
              right: 0;
              background: white;
              padding: 10px 20px;
              border-top: 1px solid #ddd;
              font-size: 10px;
              color: #666;
            }
            @media print {
              .no-print { display: none; }
              body { margin: 0; padding-bottom: 50px; }
              .header { margin-bottom: 15px; padding-bottom: 10px; }
              .data-sources {
                position: fixed;
                bottom: 0;
                left: 0;
                right: 0;
                background: white;
                padding: 10px 20px;
                border-top: 1px solid #ddd;
                font-size: 10px;
                color: #666;
              }
              @page {
                margin: 0.5in;
                size: letter;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Current Well Owner / Parcel Owner Report</h1>
            <p>Generated on ${new Date().toLocaleDateString()}</p>
            <p>Search Location: ${searchCoordinates?.[1]?.toFixed(
              6
            )}, ${searchCoordinates?.[0]?.toFixed(6)} |
               Radius: ${searchRadius} ${searchUnits} |
               Wells Found: ${wellsCount} |
               Parcels Matched: ${parcelsFoundCount} |
               Selected for Export: ${
                 selectedData.length
               } wells, ${selectedParcelsCount} parcels</p>
          </div>

          ${selectedData
            .map(
              (wellData) => `
            <div class="well-card">
              <div class="well-header">
                Well #${wellData.id}: ${wellData.wellNumber} - ${
                wellData.wellName
              }${
                wellData.parcelId === "N/A"
                  ? '<span class="chip-red">No Parcel Match</span>'
                  : ""
              }${
                wellData.propertyOwnerName === "Not found"
                  ? '<span class="chip-orange">No Parcel Contact</span>'
                  : ""
              }
              </div>

              <div class="section">
                <div class="section-title">Well Technical Information</div>
                <div class="technical-columns">
                  <div>
                    <table class="info-table">
                      <tr><td>CUWCD Number:</td><td>${
                        wellData.wellNumber
                      }</td></tr>
                      <tr><td>State Number:</td><td>${
                        wellData.stateWellNumber
                      }</td></tr>
                      <tr><td>Status:</td><td>${wellData.wellStatus}</td></tr>
                      <tr><td>Primary Use:</td><td>${
                        wellData.primaryUse
                      }</td></tr>
                      <tr><td>Source Aquifer:</td><td>${
                        wellData.aquifer
                      }</td></tr>
                      <tr><td>Coordinates:</td><td>${
                        wellData.location
                      }</td></tr>
                    </table>
                  </div>
                  <div>
                    <table class="info-table">
                      <tr><td>Well Depth:</td><td>${
                        wellData.wellDepthFt
                      }</td></tr>
                      <tr><td>Elevation:</td><td>${
                        wellData.elevationFtAmsl
                      }</td></tr>
                      <tr><td>Screen Top Depth:</td><td>${
                        wellData.screenTopDepthFt
                      }</td></tr>
                      <tr><td>Screen Bottom Depth:</td><td>${
                        wellData.screenBottomDepthFt
                      }</td></tr>
                      <tr><td>Driller:</td><td>${wellData.driller}</td></tr>
                      <tr><td>Date Drilled:</td><td>${
                        wellData.dateDrilled
                      }</td></tr>
                      <tr><td>Permit Number:</td><td>${
                        wellData.permitNumber
                      }</td></tr>
                      <tr><td>Well Type:</td><td>${wellData.wellType}</td></tr>
                    </table>
                  </div>
                </div>
              </div>

              <div class="comparison">
                <div>
                  <div class="section-title">Current Well Owner (Database Records)</div>
                  <table class="info-table">
                    <tr><td>Owner Name:</td><td>${
                      wellData.wellOwnerName
                    }</td></tr>
                    <tr><td>Organization:</td><td>${
                      wellData.wellOwnerOrganization
                    }</td></tr>
                    <tr><td>Address:</td><td>${
                      wellData.wellOwnerAddress
                    }</td></tr>
                    <tr><td>City, State ZIP:</td><td>${
                      wellData.wellOwnerCity
                    }, ${wellData.wellOwnerState} ${
                wellData.wellOwnerZip
              }</td></tr>
                    <tr><td>Phone 1:</td><td>${
                      wellData.wellOwnerPhone1
                    }</td></tr>
                    <tr><td>Phone 2:</td><td>${
                      wellData.wellOwnerPhone2
                    }</td></tr>
                    <tr><td>Email 1:</td><td>${
                      wellData.wellOwnerEmail1
                    }</td></tr>
                    <tr><td>Email 2:</td><td>${
                      wellData.wellOwnerEmail2
                    }</td></tr>
                  </table>
                </div>

                <div>
                  <div class="section-title">Parcel Owner (County Records)</div>
                  <table class="info-table">
                    <tr><td>Owner Name:</td><td>${
                      wellData.propertyOwnerName
                    }</td></tr>
                    <tr><td>Mailing Address:</td><td>${
                      wellData.propertyOwnerAddress
                    }</td></tr>
                    <tr><td>City, State ZIP:</td><td>${
                      wellData.propertyOwnerCity
                    }, ${wellData.propertyOwnerState} ${
                wellData.propertyOwnerZip
              }</td></tr>
                  </table>

                  <div style="margin-top: 15px;">
                    <div class="section-title">Parcel Information</div>
                    <table class="info-table">
                      <tr><td>Parcel ID:</td><td>${
                        wellData.parcelId !== "N/A"
                          ? `<a href="${getBellCadParcelUrl(
                              wellData.parcelId
                            )}" target="_blank" rel="noreferrer noopener" style="color: #1976d2; text-decoration: underline;">${
                              wellData.parcelId
                            }</a>`
                          : wellData.parcelId
                      }</td></tr>
                      <tr><td>Legal Acreage:</td><td>${
                        wellData.parcelAcres
                      } acres</td></tr>
                      <tr><td>Parcel Address:</td><td>${
                        wellData.situsAddress
                      }</td></tr>
                      <tr><td>Parcel City:</td><td>${
                        wellData.situsCity
                      }</td></tr>
                      <tr><td>Legal Description:</td><td>${
                        wellData.legalDescription
                      }</td></tr>
                      <tr><td>Market Value:</td><td>${
                        wellData.marketValue
                      }</td></tr>
                      <tr><td>Land Value:</td><td>${
                        wellData.landValue
                      }</td></tr>
                      <tr><td>Improvement Value:</td><td>${
                        wellData.improvementValue
                      }</td></tr>
                      <tr><td>Last Deed Date:</td><td>${
                        wellData.lastDeedDate
                      }</td></tr>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          `
            )
            .join("")}

          <!-- Data Sources Footer - appears on every page -->
          <div class="data-sources">
            <strong>Notification Contact Sources:</strong>
            Well Owner Contacts: Clearwater Underground Water Conservation District database |
            Parcel Owner Contacts: Bell County Appraisal District parcel records |
            <em>Note: Database records contain current well owner information, while parcel records provide additional property owner details.</em>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();

    // Auto-print when loaded
    printWindow.onload = () => {
      // Add a small delay to ensure content is fully loaded
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  }, [
    selectedData,
    selectedParcelsCount,
    searchCoordinates,
    searchRadius,
    searchUnits,
    wellsCount,
    parcelsFoundCount,
  ]);

  // Show loading skeleton if still loading
  if (loading) {
    return (
      <StyledDialog open={open} onClose={onClose} maxWidth={false}>
        <DialogTitle>Current Well Owner / Parcel Owner Report</DialogTitle>
        <DialogContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            py={4}
          >
            <CircularProgress size={40} style={{ marginRight: 16 }} />
            <Typography variant="h6">
              Generating Well Owner Contact Lists...
            </Typography>
          </Box>
          <Box mb={3}>
            <Skeleton variant="rect" width="100%" height={120} />
          </Box>
          <Skeleton variant="rect" width="100%" height={300} />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary" variant="outlined">
            Cancel
          </Button>
        </DialogActions>
      </StyledDialog>
    );
  }

  if (!reportData) return null;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      aria-labelledby="wells-report-dialog-title"
      maxWidth={false}
    >
      <DialogTitle id="wells-report-dialog-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Current Well Owner / Parcel Owner Report
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            size="small"
            edge="end"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Summary Section */}
        <SummaryCard>
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1.5}
            >
              <Typography variant="h6">Search Summary</Typography>
              <Box display="flex">
                <Button
                  variant="outlined"
                  startIcon={<GetAppIcon />}
                  onClick={exportToCSV}
                  size="small"
                  style={{ marginRight: 12 }}
                  disabled={selectedWells.size === 0}
                >
                  Export CSV ({selectedData.length})
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FileCopyIcon />}
                  onClick={exportToPDF}
                  size="small"
                  disabled={selectedWells.size === 0}
                >
                  Export PDF ({selectedData.length})
                </Button>
              </Box>
            </Box>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="textSecondary">
                  Search Location
                </Typography>
                <Typography variant="body1">
                  {searchCoordinates?.[1]?.toFixed(6)},{" "}
                  {searchCoordinates?.[0]?.toFixed(6)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="textSecondary">
                  Search Radius
                </Typography>
                <Typography variant="body1">
                  {searchRadius} {searchUnits}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="textSecondary">
                  Wells Found
                </Typography>
                <Chip
                  label={`${searchInput ? filteredData.length : wellsCount} ${
                    searchInput ? "filtered" : "total"
                  }`}
                  color="primary"
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="textSecondary">
                  Parcels Matched
                </Typography>
                <Chip
                  label={`${
                    searchInput ? filteredParcelsCount : parcelsFoundCount
                  } ${searchInput ? "filtered" : "total"}`}
                  style={{
                    backgroundColor: "#7b1fa2",
                    color: "#ffffff",
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                    fontSize: "0.75rem",
                    fontWeight: 500,
                  }}
                  size="small"
                />
              </Grid>
            </Grid>
          </CardContent>
        </SummaryCard>

        {/* Selection Info Alert */}
        <Alert severity="info" style={{ marginBottom: 16 }}>
          <Typography variant="body2">
            <strong>Export Selection:</strong> Use the checkboxes below to
            select which wells to include in your CSV/PDF exports. All wells are
            selected by default. Search to filter the list, then uncheck any
            wells you don't want to notify.
          </Typography>
        </Alert>

        {/* Selection Controls */}
        <Box mb={1.5} display="flex" alignItems="center">
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginRight: 12 }}
          >
            {selectedWells.size} of {filteredData.length} wells selected for
            export
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={selectAllVisible}
            disabled={selectedWells.size === filteredData.length}
            style={{ marginRight: 6 }}
          >
            Select All Filtered Records
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={handleSelectNone}
            disabled={selectedWells.size === 0}
          >
            Select None
          </Button>
        </Box>

        {/* Search and Pagination Controls */}
        {wellsCount >= 2 && (
          <Box mb={2}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={wellsCount > WELLS_PER_PAGE ? 6 : 12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder="Search wells by number, name, owner, status..."
                  value={searchInput}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              {wellsCount > WELLS_PER_PAGE && (
                <Grid item xs={12} sm={6}>
                  <Box
                    display="flex"
                    justifyContent="flex-end"
                    alignItems="center"
                  >
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      style={{ marginRight: 12 }}
                    >
                      Showing {startIndex + 1}-
                      {Math.min(
                        startIndex + WELLS_PER_PAGE,
                        filteredData.length
                      )}{" "}
                      of {filteredData.length}
                    </Typography>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      size="small"
                      color="primary"
                    />
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Wells Comparison */}
        <ComparisonContainer>
          {paginatedData.map((wellData) => (
            <WellDataCard
              key={wellData.id}
              wellData={wellData}
              selectedWells={selectedWells}
              handleSelectWell={handleSelectWell}
            />
          ))}
        </ComparisonContainer>

        {/* Data Source Information */}
        <Box mt={2}>
          <Typography variant="body2" color="textSecondary">
            <strong>Notification Contact Sources:</strong>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            • Well Owner Contacts: Clearwater Underground Water Conservation
            District database
          </Typography>
          <Typography variant="body2" color="textSecondary">
            • Parcel Owner Contacts: Bell County Appraisal District parcel
            records
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 6 }}
          >
            <strong>Parcel Matching Process:</strong> Wells are spatially
            matched to parcels using GPS coordinates and polygon geometry. A red
            "No Parcel Match" chip indicates the well coordinates fall outside
            all parcel boundaries. An orange "No Parcel Contact" chip indicates
            a parcel was found but missing owner contact information.
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 6 }}
          >
            <em>
              Note: Use both contact sources to ensure all relevant parties
              contact information. Database records contain current well owner
              contacts while county records reflect current parcel ownership and
              mailing addresses.
            </em>
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="primary" variant="outlined">
          Close
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default WellsReportDialog;
